/**
 * Centralised copy for the Store Inventory → Reports tab.
 *
 * Every visible metric, chart, and column should have an entry here so users
 * can hover the eye icon and understand what they are looking at. Keep each
 * string short — 1 to 3 sentences — and concrete.
 *
 * HOUSE RULE (F-FE-N99): plain language only. Banned words in customer-facing
 * copy: "turnover", "ledger", "cost layers", "unexplained", "variance" (say
 * "difference"). If a sentence needs accounting vocabulary to be true, the
 * metric is wrong, not the copy.
 */

// --- Page intro --------------------------------------------------------------

export const REPORTS_TAB_INTRO =
  'Four questions, answered from your recorded purchases, sales and counts: what your stock is worth, what you used, what you lost, and what needs your attention.'

// --- What's my stock worth? --------------------------------------------------

export const STOCK_NOW_VALUE =
  'What everything on your shelves is worth right now: quantity on hand × what it cost you to buy. Items showing less than zero are excluded and flagged separately.'

export const STOCK_NOW_DONUT =
  'How that value splits across your categories. One category holding almost everything usually means one big item — check it has been counted.'

export const STOCK_NOW_NEGATIVE =
  'The system says you have less than zero of these — impossible in reality, so the recorded number is wrong. Counting the item fixes it in one step.'

// --- What did you use? -------------------------------------------------------

export const ACTIVITY_USED =
  'The cost of everything that left your shelves for customers and the kitchen this period: ingredients used by sales (via recipes) plus anything issued for kitchen or staff use. Waste is counted separately, under losses.'

export const ACTIVITY_PURCHASED =
  'What you spent restocking this period, valued from your recorded purchases and deliveries.'

export const ACTIVITY_DAYS_LEFT =
  'At this period’s pace of use, roughly how many days your current stock lasts. A rough guide for ordering, not a promise.'

export const ACTIVITY_USED_PCT =
  'How much of your current stock value you went through this period. Very low can mean over-stocking; check the slow movers.'

export const ACTIVITY_TREND =
  'Money in (purchases) next to money out (usage), per day or week. Buying consistently more than you use means stock is piling up.'

// --- What are you losing? ----------------------------------------------------

export const LOSSES_HEADLINE =
  'Money lost this period: stock thrown away or damaged, plus stock your counts found missing.'

export const LOSSES_WASTE =
  'Stock written off as spoiled, wasted or damaged, valued at what it cost you. Repeated items here point to a storage, rotation or portioning problem.'

export const LOSSES_SHRINKAGE =
  'When you count an item and find less than the system expected, the missing amount lands here. It is the gap between the shelf and the records — miscounts, unlogged waste, or theft.'

export const LOSSES_FOUND =
  'Counts that found MORE than expected. Usually an earlier miscount or an unrecorded delivery — not free stock.'

export const LOSSES_VENDOR_RETURNS =
  'Stock sent back to suppliers. Not a loss — the money usually comes back as a refund or credit.'

export const LOSSES_COUNT_TABLE =
  'Every count this period and the difference it found. “Since last count” columns show what the records say happened in between, so you can judge whether the gap is explained.'

// --- What needs your attention? ---------------------------------------------

export const ATTENTION_INTRO =
  'The short list to act on: items to reorder, and items whose recorded quantity deserves a check.'

export const ATTENTION_COUNT_THESE =
  'Items worth physically counting now: high-value items nobody has ever verified, items not counted in a while, and items showing less than zero. Counting takes a minute and fixes the number on the spot.'

export const ATTENTION_LOW_STOCK =
  'Items at or below the reorder point you set. Items with no reorder point set are not included — set one on the item to be warned.'

// --- Waste table columns -----------------------------------------------------

export const COL_CATEGORY = 'The category this item belongs to.'
export const COL_WASTE_ITEM = 'The item that was written off.'
export const COL_WASTE_QTY = 'How much was thrown away (in the item’s unit).'
export const COL_WASTE_REASON = 'The reason chosen when it was written off.'
export const COL_WASTE_VALUE = 'What the wasted amount cost you to buy.'
export const COL_WASTE_DATE = 'When the write-off was recorded.'
